import { useContext, useState } from 'react';
import type { ClientRelationWorklog } from '../models';
import { CIRCLE_COLORS_ELEMENT } from '../constants/circleColors';
import ExpandArrowIcon from '../icons/ExpandArrowIcon';
import { NoWorkLogIcon } from '../icons/NoWorkLogIcon';
import LocalizationHelper from '../../../../kanban/helpers/TranslationHelper';
import LocalizationContext from '../../../../kanban/shared/contexts/LocalizationContext';
import { formatWorklogDuration, formatWorklogTime } from '../helpers/WorklogTimeHelper';
import { DropdownFilter, type DropdownFilterValueProps } from './DropdownFilter';
import type { TimeFormat, TimeZone } from './TimeSettingsDropDown';
import WorklogInfo from './WorklogInfo';

export type WorkLogListProps = {
  value: ClientRelationWorklog[];
  timeZone: TimeZone;
  timeFormat: TimeFormat;
};

type FilterValue = {
  key: 'Total' | 'Tracked' | 'Manual' | 'Overtime';
};

const WorkLogList = ({ value, timeZone, timeFormat }: WorkLogListProps) => {
  const localization = useContext(LocalizationContext);
  const [openedWorklogId, setOpenWorklogId] = useState(0);
  const [filter, setFilter] = useState<FilterValue['key']>('Total');

  const noWorkLogTitle = LocalizationHelper.getLocaleEntityValue(
    localization,
    'ZSite.Frontend.Worklog.Detailed.NoWorkLogTitle',
    'Nothing to show - yet!'
  );
  const noWorkLogDescription = LocalizationHelper.getLocaleEntityValue(
    localization,
    'ZSite.Frontend.Worklog.Detailed.NoWorkLogDescription',
    'We’ll display your recent updates here as soon as something happens'
  );

  const getWorklogStatusCircle = (worklog: ClientRelationWorklog) => {
    if (worklog.HasOvertime) return CIRCLE_COLORS_ELEMENT.overtime;
    if (worklog.IsLoggedAutomatically) return CIRCLE_COLORS_ELEMENT.trackedWithTracker;
    return CIRCLE_COLORS_ELEMENT.manually;
  };

  const dropdownFilterItems: DropdownFilterValueProps<FilterValue>[] = [
    {
      label: 'Total time',
      value: { key: 'Total' },
    },
    {
      label: 'Tracked',
      value: { key: 'Tracked' },
    },
    {
      label: 'Manual',
      value: { key: 'Manual' },
    },
    {
      label: 'Overtime',
      value: { key: 'Overtime' },
    },
  ];

  const clientRelationWorklogs = value.filter((item) => {
    if (filter === 'Tracked') return item.IsLoggedAutomatically;
    if (filter === 'Manual') return !item.IsLoggedAutomatically;
    if (filter === 'Overtime') return item.HasOvertime;
    return true;
  });

  const getWorklogTime = (worklog: ClientRelationWorklog) => {
    if (!worklog.LogStart || !worklog.LogEnd) return <span>N/A</span>;

    return (
      <div className="worklog-item-text-block">
        <div>{getWorklogStatusCircle(worklog)}</div>
        <span>
          {formatWorklogTime(worklog.LogStart, timeZone, timeFormat)} -{' '}
          {formatWorklogTime(worklog.LogEnd, timeZone, timeFormat)} (
          {formatWorklogDuration(worklog.LogStart, worklog.LogEnd)} hrs)
        </span>
      </div>
    );
  };

  return (
    <div className="worklogs">
      <div className="worklog-list-header">
        <h4>Total time</h4>
        <DropdownFilter
          items={dropdownFilterItems}
          initValue={dropdownFilterItems[0].value}
          onChange={(selected) => setFilter(selected.key)}
        />
      </div>
      <div className="table-worklogs">
        <div className="table-header-row">Time details</div>
        {clientRelationWorklogs.length ? (
          clientRelationWorklogs.map((worklog) => {
            const isWorklogChosen = openedWorklogId === worklog.CrmWorkLogId;
            return (
              <div
                key={worklog.CrmWorkLogId}
                className={`worklog-item ${isWorklogChosen ? 'worklog-item-chosen' : ''}`}
              >
                <div
                  className="table-worklogs-entry"
                  onClick={() => setOpenWorklogId(isWorklogChosen ? 0 : worklog.CrmWorkLogId)}
                >
                  {getWorklogTime(worklog)}
                  <div style={{ transform: isWorklogChosen ? 'rotate(180deg)' : '' }}>
                    <ExpandArrowIcon />
                  </div>
                </div>
                <div className="worklog-item-worklog-info">
                  {isWorklogChosen && (
                    <WorklogInfo timeFormat={timeFormat} timeZone={timeZone} value={worklog} />
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-worklog-detail-block">
            <div className="no-worklog-detail-block-icon">
              <NoWorkLogIcon />
            </div>
            <div>{noWorkLogTitle}</div>
            <div>{noWorkLogDescription}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkLogList;
